import { Button } from '@mui/material';
import { useState, useCallback, useRef, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm';
import { CameraVideoCanvas2 } from './CameraVideoCanvas2';

function normalizeDecodedBarcodeText(text: string, format?: string): string {
  const normalizedFormat = (format ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const textWithoutSymbologyIdentifier = text.startsWith(']C1') ? text.slice(3) : text;
  const isNumeric = /^\d+$/.test(textWithoutSymbologyIdentifier);
  const isUpcAWithLeadingZero =
    isNumeric
    && textWithoutSymbologyIdentifier.length === 13
    && textWithoutSymbologyIdentifier.startsWith('0')
    && (normalizedFormat === 'UPCA' || normalizedFormat === 'EAN13');

  if (isUpcAWithLeadingZero) {
    return textWithoutSymbologyIdentifier.slice(1);
  }

  const isLikelyGs1Ai01 =
  normalizedFormat === 'CODE128'
  && /^01\d{14}/.test(textWithoutSymbologyIdentifier)
  && !textWithoutSymbologyIdentifier.startsWith('(01)');

  if (isLikelyGs1Ai01) {
    return `(01)${textWithoutSymbologyIdentifier.slice(2)}`;
  }

  return textWithoutSymbologyIdentifier;
}

// Normalize GS1-128 result: remove GS (group separator) chars for consistent comparison
function normalizeGs1Code(code: string): string {
  const withoutSymbologyIdentifier = code.startsWith(']C1') ? code.slice(3) : code;
  return withoutSymbologyIdentifier
    .split(String.fromCharCode(29)).join('')
    .replace(/<GS>/gi, '');
}

function isDigits(value: string): boolean {
  if (!value) return false;
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}

function getComparableScanCode(code: string): string {
  const normalized = normalizeGs1Code(code).trim();

  // Compare AI(01) labels against parsed GTIN values as the same identifier.
  if (normalized.startsWith('(01)') && normalized.length >= 18) {
    const ai01Value = normalized.slice(4, 18);
    if (isDigits(ai01Value)) {
      return ai01Value;
    }
  }

  if (normalized.startsWith('01') && normalized.length >= 16) {
    const ai01Value = normalized.slice(2, 16);
    if (isDigits(ai01Value)) {
      return ai01Value;
    }
  }

  return normalized;
}

async function decodeBarcodeWasm(imageData: ImageData): Promise<string | null> {
  const results = await readBarcodes(imageData, {
    formats: [
      'UPC-A', 'UPC-E', 'EAN-13', 'EAN-8', 'DataBar', 'DataBarExpanded', 'DataBarLimited',
      'Code128', 'ITF', 'Code39',
    ],
    tryHarder: true,
    maxNumberOfSymbols: 1,
  });
  if (results.length > 0) {
    const firstResult = results[0] as { text: string; format?: string };
    return normalizeDecodedBarcodeText(firstResult.text, firstResult.format);
  }
  return null;
}

interface WasmBarcodeScannerProps {
  onLog?: (msg: string) => void;
  onCapture: (code: string) => void;
  lastScannedCode?: string;
}

export const WasmBarcodeScanner2 = ({ onLog, onCapture, lastScannedCode = '' }: WasmBarcodeScannerProps) => {
  const [scannedCode, setScannedCode] = useState<string>(lastScannedCode);
  const [scanRequested, setScanRequested] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanBuffer, setScanBuffer] = useState<string[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const scanStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    setScannedCode(lastScannedCode);
  }, [lastScannedCode]);

  const isCameraPermissionError = (err: unknown): boolean => {
    if (err instanceof DOMException) {
      return err.name === 'NotAllowedError' || err.name === 'SecurityError';
    }

    if (typeof err === 'object' && err !== null) {
      const name = 'name' in err ? String((err as { name?: unknown }).name ?? '') : '';
      const message = 'message' in err ? String((err as { message?: unknown }).message ?? '') : '';
      return name === 'NotAllowedError'
        || name === 'SecurityError'
        || /permission denied|notallowederror|securityerror/i.test(message);
    }

    return false;
  };


  // Timeout for "no barcode detected" during an active scan attempt.
  useEffect(() => {
    if (scanRequested) {
      scanStartTimeRef.current = Date.now();
      const timeout = setTimeout(() => {
        setScanError('No barcode detected. Hold steady, move closer, and keep the barcode inside the frame.');
        setFailureCount(f => f + 1);
        setIsScanning(false);
        setScanRequested(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [scanRequested]);
  useEffect(() => {
    prepareZXingModule({
      overrides: {
        locateFile: () => '/wasm/zxing_full.wasm',
      },
    });
  }, []);

  // Camera permission error handling
  const handleCameraPermissionError = useCallback(() => {
    setCameraError('Camera access is required to scan. Enable permissions and try again.');
    setScanRequested(false);
    setIsScanning(false);
  }, []);

  // Main frame handler
  const handleFrame = useCallback(async (imageData: ImageData) => {
    if (!scanRequested || isScanning) return;
    setIsScanning(true);

    try {
      const result = await decodeBarcodeWasm(imageData);
      if (result) {
        onLog?.(`Raw scan result: ${result}`);
        const normalizedResult = getComparableScanCode(result);
        const normalizedLastScannedCode = getComparableScanCode(lastScannedCode);
        const shouldReuseParsedLastScannedCode = lastScannedCode.length > 0
          && normalizedLastScannedCode === normalizedResult;
        const displayCode = shouldReuseParsedLastScannedCode ? lastScannedCode : result;
 

        setScannedCode(displayCode);
        setScanBuffer([]);
        setScanError(null);
        setFailureCount(0);
        onLog?.(`Scanned: ${displayCode}`);
        setScanRequested(false);
        setIsScanning(false);
        onCapture(result);
      } else {
        // If 15s has not elapsed, treat as a normal failure
        if (scanStartTimeRef.current && Date.now() - scanStartTimeRef.current < 60000) {
          setFailureCount(f => {
            const newCount = f + 1;
            if (newCount >= 3) {
              setScanError('Still having trouble reading this barcode. Try better lighting, reduce glare, or move closer.');
            } else {
              setScanError('No barcode detected. Hold steady, move closer, and keep the barcode inside the frame.');
            }
            return newCount;
          });
        }
        setIsScanning(false);
        // Do not reset scanBuffer on a single miss; keep recent candidates for GS1 stability.
      }
    } catch (err: unknown) {
      if (isCameraPermissionError(err)) {
        handleCameraPermissionError();
      } else {
        onLog?.(`Scanner error: ${err instanceof Error ? err.message : String(err)}`);
        setScanError('Unable to read from camera right now. Please try again.');
        setScanRequested(false);
      }
      setIsScanning(false);
      setScanRequested(false);  
    }
  }, [scanRequested, isScanning, onLog, onCapture, handleCameraPermissionError, scannedCode, lastScannedCode]);

  const handleScanButtonClick = () => {
    setScanRequested(true);
    setScanBuffer([]);
    setScanError(null);
    setFailureCount(0);
    setCameraError(null);
  };

  // Error message style at the top
  const errorStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    background: 'rgba(200,0,0,0.85)',
    color: '#fff',
    padding: '10px 8px',
    borderRadius: '8px 8px 0 0',
    textAlign: 'center',
    fontSize: 16,
    zIndex: 2,
    pointerEvents: 'none',
    minHeight: 24,
  };

  const loadingOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'rgba(0,0,0,0.75)',
    borderRadius: 8,
    zIndex: 3,
    color: '#fff',
    fontSize: 15,
  };

  // Layout fix: stack button and code vertically, keep compact
  return (
    <div style={{ position: 'relative', width: '100%',maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ position: 'relative', width: '100%', flexGrow: 1, flexShrink: 1, minHeight: 0, background: '#222', borderRadius: 8, overflow: 'hidden' }}>
        {!isCameraReady && (
          <div style={loadingOverlayStyle}>

           
            <CircularProgress color="inherit"
              size={40} />
            <span>Starting camera...</span>
          </div>
        )}
        {(scanError || cameraError) && (
          <div style={errorStyle}>
            {cameraError || scanError}
          </div>
        )}
    
        <CameraVideoCanvas2
          onFrame={handleFrame}
          onLog={onLog}
          onReady={() => setIsCameraReady(true)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, marginTop: 12, flexShrink: 0, paddingLeft: 5, paddingRight:5 }}>
        <Button
          variant="primary"
          color="primary"
          onClick={handleScanButtonClick}
          disabled={!isCameraReady || scanRequested || isScanning}
          style={{ width: '100%' }}
        >
          {(scanRequested || isScanning) ? 'Scanning...' : 'Scan Barcode'}
        </Button>
        <div style={{
          background: '#f5f5f5',
          borderRadius: 6,
          padding: '8px 12px',
          textAlign: 'center',
          fontSize: 16,
          minHeight: 32,
          marginTop: 4,
          wordBreak: 'break-all',
        }}>
          <strong>Last scanned code:</strong> {scannedCode}
        </div>
      </div>
    </div>
  );
};